import { memo, useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';

import Text from '@zenbusiness/zui/Text';

import ActivePromptInput from '../ActivePromptInput';
import * as Styled from './AssistantWelcome.style';
import { WELCOME_PROMPT_BUTTONS } from '../utils/constants';

/**
 * AssistantWelcome Component
 *
 * A welcome screen component that displays the assistant chat interface introduction.
 * Features predefined prompt buttons that populate the input field and a message input area to start conversations.
 *
 * @param {object} props - Component props
 * @param {Function} props.onMessageSend - Callback function when a message is sent
 * @param {Function} props.onPromptSelect - Callback function when a prompt button is clicked (only needed if autoSubmitPrompts is false)
 * @param {object} props.activePromptInputRef - Ref to ActivePromptInput component
 * @param {boolean} [props.disabled=false] - Whether the component is disabled
 * @param {boolean} [props.autoSubmitPrompts=false] - If true, clicking a prompt sends immediately. If false, it populates the input field.
 * @param {string} [props.title] - Title text to display
 */

// Business is complicated. Velo makes it easy.
//  Your AI guide that helps you start, protect, and grow your business—all in one place.
const AssistantWelcome = memo(
  ({
    onMessageSend,
    onPromptSelect,
    activePromptInputRef,
    disabled = false,
    autoSubmitPrompts = true,
    title = 'Business is complicated. \n Velo makes it easy.',
    subtitle = 'Your AI guide that helps you start, protect, and grow your business—all in one place.'
  }) => {
    // Auto-focus the input when component mounts
    useEffect(() => {
      if (activePromptInputRef?.current?.focus && !disabled) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          activePromptInputRef?.current?.focus();
        });
      }
    }, [activePromptInputRef, disabled]);

    const handlePromptClick = useCallback(
      (promptMessage) => {
        if (disabled) return;

        // If autoSubmitPrompts is true, send the message immediately
        // Otherwise, populate the input field for the user to edit/send
        if (autoSubmitPrompts) {
          if (onMessageSend) {
            onMessageSend(promptMessage);
          }
        } else if (onPromptSelect) {
          onPromptSelect(promptMessage);
        }
      },
      [disabled, autoSubmitPrompts, onMessageSend, onPromptSelect]
    );

    const handleCustomMessage = useCallback(
      (message) => {
        if (disabled || !onMessageSend) {
          return;
        }

        onMessageSend(message);
      },
      [disabled, onMessageSend]
    );

    return (
      <Styled.WelcomeContainer>
        <Styled.WelcomeContent>
          <Styled.WelcomeTitle>
            {title.split('\n').map((line, index) => (
              <Styled.WelcomeTitleText key={index} color="primary" size="xl">
                {line}
                {index < title.split('\n').length - 1 && <br />}
              </Styled.WelcomeTitleText>
            ))}
            <Text color="primary" mt={2} size="md">
              {subtitle}
            </Text>
          </Styled.WelcomeTitle>

          <Styled.PromptsContainer>
            {WELCOME_PROMPT_BUTTONS.map((prompt) => (
              <Styled.PromptButton
                key={prompt.id}
                disabled={disabled}
                size="medium"
                variant="outlined"
                onClick={() => handlePromptClick(prompt.text)}
              >
                {prompt.text}
              </Styled.PromptButton>
            ))}
          </Styled.PromptsContainer>
        </Styled.WelcomeContent>

        {/* Input Area */}
        <Styled.WelcomeInputArea>
          <ActivePromptInput
            ref={activePromptInputRef}
            disabled={disabled}
            placeholder="Ask the assistant anything..."
            onSendMessage={handleCustomMessage}
          />
        </Styled.WelcomeInputArea>
      </Styled.WelcomeContainer>
    );
  }
);

AssistantWelcome.propTypes = {
  onMessageSend: PropTypes.func.isRequired,
  onPromptSelect: PropTypes.func,
  activePromptInputRef: PropTypes.object,
  disabled: PropTypes.bool,
  autoSubmitPrompts: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string
};

export default AssistantWelcome;
